import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthState } from '@/hooks/useAuthState';
import { supabase } from '@/lib/supabase';
import { readinessQuestions } from '@/data/readiness';
import { storePendingAssessment } from '@/utils/assessmentStorage';

export const useReadinessAssessment = () => {
  const [currentDimension, setCurrentDimension] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [isComplete, setIsComplete] = useState(false);
  const [scores, setScores] = useState<Record<number, number>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuthState();

  const getGlobalQuestionIndex = useCallback((dimensionIndex: number, localQuestionIndex: number) => {
    let globalIndex = 0;
    for (let i = 0; i < dimensionIndex; i++) {
      globalIndex += readinessQuestions[i].questions.length;
    }
    return globalIndex + localQuestionIndex;
  }, []);

  const dimensionAnswers = useMemo(() => {
    console.log('Calculating dimension answers from:', answers);
    const startIndex = readinessQuestions
      .slice(0, currentDimension)
      .reduce((acc, dim) => acc + dim.questions.length, 0);

    const currentDimLength = readinessQuestions[currentDimension].questions.length;
    const subset: Record<number, string> = {};
    
    for (let i = 0; i < currentDimLength; i++) {
      const globalIndex = startIndex + i;
      subset[i] = answers[globalIndex] || '';
    }
    
    console.log('Calculated dimension answers:', subset);
    return subset;
  }, [answers, currentDimension]);

  const handleAnswerChange = useCallback((localIndex: number, value: string) => {
    const globalIndex = getGlobalQuestionIndex(currentDimension, localIndex);
    setAnswers(prev => {
      const newAnswers = { ...prev, [globalIndex]: value };
      setHasChanges(true);
      return newAnswers;
    });
  }, [currentDimension, getGlobalQuestionIndex]);

  const calculateScores = useCallback(() => {
    const dimensionScores: Record<number, number> = {};
    
    readinessQuestions.forEach((dimension, dimIndex) => {
      let totalScore = 0;
      let answeredQuestions = 0;
      
      dimension.questions.forEach((question, qIndex) => {
        const globalQuestionIndex = getGlobalQuestionIndex(dimIndex, qIndex);
        const answer = answers[globalQuestionIndex];
        
        if (answer && question.options) {
          const score = question.options.indexOf(answer) + 1;
          totalScore += score;
          answeredQuestions++;
        }
      });
      
      const avgScore = answeredQuestions > 0 ? totalScore / answeredQuestions : 0;
      dimensionScores[dimension.id] = avgScore;
    });

    return dimensionScores;
  }, [answers, getGlobalQuestionIndex]);

  const handleSave = useCallback(async (): Promise<void> => {
    if (!user) {
      storePendingAssessment({
        answers,
        currentDimension,
        returnTo: '/readiness/assessment/wizard'
      });
      
      navigate('/login');
      return Promise.reject('User not logged in');
    }
    
    try {
      const calculatedScores = calculateScores();
      
      const { error } = await supabase
        .from('readiness_assessments')
        .insert({
          user_id: user.id,
          mode: 'standard',
          data: answers,
          scores: calculatedScores
        });

      if (error) throw error;
      
      setScores(calculatedScores);
      setHasChanges(false);
      toast.success('Progress saved successfully');
      return Promise.resolve();
    } catch (error) {
      console.error('Error saving assessment:', error);
      toast.error('Error saving assessment');
      return Promise.reject(error);
    }
  }, [user, answers, currentDimension, navigate, calculateScores]);

  return {
    currentDimension,
    setCurrentDimension,
    answers,
    setAnswers,
    isComplete,
    setIsComplete,
    scores,
    setScores,
    hasChanges,
    setHasChanges,
    dimensionAnswers,
    handleAnswerChange,
    calculateScores,
    handleSave,
  };
};